import {ITestheftAPI, ITestheftAPI_aufgabe, ITestheftAPI_item, ITestheftAPI_modul} from './Interfaces';
import {IDidaktische_kommentare_itemsDB, IDomaenenDB} from './InterfacesDB';
import Bildungsstandards from './Bildungsstandards';
import Tooltip from './Tooltip';
import TooltipBase from './TooltipBase';
import {getDomaenenKurzbeschreibungUrl, getTooltipMaterialien, renderTooltipDatenhinweis} from './TooltipMaterialien';
import Utilities from './Utilities';

const AUFGABENBILD_BASISPFAD = '/materialien/vera5/aufgaben-bilder';
const BASE_AUFG_ID_PATTERN = /^26v5[a-z0-9_]+$/;
const BOOTSTRAP_ICON_SPRITE = '/lib/bootstrap-icons/bootstrap-icons/bootstrap-icons.svg';
const TOOLTIP_UEBERSCHRIFTEN: Readonly<Record<string, string>> = {
    'Leitidee': Tooltip.html`<p>Leitideen gliedern die inhaltsbezogenen Kompetenzen in grundlegende mathematische Inhaltsbereiche. Sie unterstützen den vernetzten Aufbau mathematischer Inhalte über die Schulstufen hinweg.</p>`,
    'Domäne': Tooltip.html`<p>Eine Domäne ist ein fachlicher Kompetenzbereich, beispielsweise Lesen oder Zuhören. Sie bündelt die Kompetenzen eines zusammengehörigen Inhaltsbereichs.</p>`,
    'Item-Niveaustufe': Tooltip.html`<p>Die Item-Niveaustufe ordnet die Anforderungen des Items einem Kompetenzniveau zu. Sie stammt aus den mit dem Item gelieferten Metadaten.</p>`,
    'Pilotierungswert': Tooltip.html`<p>Der Pilotierungswert gibt an, wie häufig das Item in der für Ihre Schulform verwendeten Pilotierungsstichprobe richtig gelöst wurde.</p>`,
    'Landeswert': Tooltip.html`<p>Der Landeswert gibt an, wie häufig das Item im Landesschnitt der für Ihre Schule ausgewählten Testheftvariante richtig gelöst wurde.</p>`,
    'Anforderungsbereich': Tooltip.html`<p>Anforderungsbereiche beschreiben die Art und kognitive Komplexität der Tätigkeiten, die zum Lösen einer Aufgabe erforderlich sind. Sie sind nicht mit der empirischen Aufgabenschwierigkeit gleichzusetzen.</p>`,
    'Kompetenzstufe': Tooltip.html`<p>Die Kompetenzstufe beschreibt im Kompetenzstufenmodell, welche fachlichen Anforderungen das Item repräsentiert. Diese Angabe stammt aus der didaktischen Einordnung des Items.</p>`,
    'Allgemeine Kompetenz': Tooltip.html`<p>In Mathematik beschreiben allgemeine beziehungsweise prozessbezogene Kompetenzen, auf welche Weise mathematisch gearbeitet wird, etwa durch Argumentieren, Modellieren oder Problemlösen.</p>`,
    'Inhaltsbezogene Kompetenz': Tooltip.html`<p>Inhaltsbezogene Kompetenzen beschreiben das konkrete fachliche Wissen und Können innerhalb einer mathematischen Leitidee. Sie sind stets mit den allgemeinen beziehungsweise prozessbezogenen Kompetenzen verknüpft.</p>`
};

/** Bündelt ein Item mit seinem fachlichen Kontext im Testheft */
interface ITooltipItemData {
    testheft: ITestheftAPI;
    modul: ITestheftAPI_modul;
    aufgabe: ITestheftAPI_aufgabe;
    item: ITestheftAPI_item;
}

/** Zeigt zu einem mit `data-tooltip-item-id` markierten Itemnamen zusätzliche Iteminformationen an */
export default class TooltipItem extends TooltipBase {
    private readonly itemIndex: ReadonlyMap<string, ITooltipItemData>; // Zugriff auf Itemdaten im konkreten Testheftkontext
    private readonly kommentarIndex: ReadonlyMap<number, IDidaktische_kommentare_itemsDB>; // didaktische Zusatzinformationen über die Item-ID

    /** Bereitet Itemdaten, Popover, Tooltip-Auslöser und Benutzerinteraktionen vor */
    public constructor(testhefte: readonly ITestheftAPI[], kommentare: readonly IDidaktische_kommentare_itemsDB[], private readonly domaenen: readonly IDomaenenDB[], private readonly land: string, private readonly projektweiche: string, private readonly verwendetGymnasialePilotierungswerte: boolean, private readonly isVergleichswertFreigegeben: boolean) {
        super({
            id: 'tooltip-item',
            cssKlasse: 'tooltip-item',
            inhaltCssKlasse: 'tooltip-item-content',
            ariaLabel: 'Iteminformationen',
            triggerSelektor: 'span[data-tooltip-item-id]',
            triggerAttribute: ['data-tooltip-item-id', 'data-tooltip-testheft-id']
        });

        // Iteminformationen einmal aufbereiten, damit sie beim Öffnen nicht erneut gesucht werden müssen
        this.itemIndex = this.createItemIndex(testhefte);
        this.kommentarIndex = this.createKommentarIndex(kommentare);
    }

    /** Ermittelt die lokale Kurzbeschreibung einer Leitidee beziehungsweise Domäne */
    public static getDomaenenKurzbeschreibungUrl(projektweiche: string, fach: string, domaene: string): string | null {
        return getDomaenenKurzbeschreibungUrl(projektweiche, fach, domaene);
    }

    /** Liefert die portalweit einheitliche Kurzbezeichnung eines Items */
    public getItemBezeichnung(testheftId: number, itemId: number): string | null {
        const data = this.itemIndex.get(this.createIndexKey(testheftId, itemId)) ?? null;
        return (data === null) ? null : this.createItemBezeichnung(data);
    }

    /** Erstellt ein Nachschlagewerk, das jede Item-ID mit Testheft, Modul und Aufgabe verbindet */
    private createItemIndex(testhefte: readonly ITestheftAPI[]): ReadonlyMap<string, ITooltipItemData> {
        const itemIndex = new Map<string, ITooltipItemData>();

        // Die Teststruktur von außen nach innen bis zu den einzelnen Items durchlaufen
        testhefte.forEach(testheft => {
            testheft.module.forEach(modul => {
                modul.aufgaben.forEach(aufgabe => {
                    aufgabe.items.forEach(item => {
                        itemIndex.set(this.createIndexKey(testheft.testheft_id, item.item_id), {testheft, modul, aufgabe, item});
                    });
                });
            });
        });

        return itemIndex;
    }

    /** Erstellt ein Nachschlagewerk für die didaktischen Zusatzinformationen */
    private createKommentarIndex(kommentare: readonly IDidaktische_kommentare_itemsDB[]): ReadonlyMap<number, IDidaktische_kommentare_itemsDB> {
        const kommentarIndex = new Map<number, IDidaktische_kommentare_itemsDB>();
        kommentare.forEach(kommentar => {
            if (kommentar.item_id !== undefined)
                kommentarIndex.set(kommentar.item_id, kommentar);
        });

        return kommentarIndex;
    }

    /** Verbindet Testheft und Item zu einem eindeutigen Indexschlüssel */
    private createIndexKey(testheftId: number, itemId: number): string {
        return `${testheftId}:${itemId}`;
    }

    /** Erzeugt die Itembezeichnung aus dem konkreten Testheftkontext */
    private createItemBezeichnung(data: ITooltipItemData): string {
        const domaene = this.domaenen.find(eintrag => eintrag.domaene === data.item.domaene) ?? null;
        const domaenenKurzbezeichnung = (data.testheft.fach === 'ma') ? '' : `${domaene?.kurzbezeichnung ?? ''} `;
        const schwierigkeit = Utilities.printSchwierigkeit(data.testheft.fach, data.modul.schwierigkeit);
        const aufgabePosition = (this.projektweiche === 'vera5')
            ? Utilities.getVera5Aufgabenposition(data.testheft, data.aufgabe.aufgabe_id, data.aufgabe.position_nr)
            : data.aufgabe.position_nr;

        return (this.projektweiche === 'vera5')
            ? Utilities.printVera5Aufgabenposition(aufgabePosition, data.item.position_bezeichnung, data.aufgabe.items.length)
            : `${domaenenKurzbezeichnung}${data.modul.typ.toUpperCase()} ${schwierigkeit} ${data.aufgabe.position_nr}.${data.item.position_bezeichnung}`.replace(/\s+/g, ' ').trim();
    }

    /** Liest die Itemdaten des Auslösers und baut dessen Informationen auf */
    protected renderTrigger(trigger: HTMLElement): boolean {
        const itemIdRaw = trigger.dataset.tooltipItemId ?? '';
        const testheftIdRaw = trigger.dataset.tooltipTestheftId ?? '';
        const itemId = Number(itemIdRaw);
        const testheftId = Number(testheftIdRaw);

        if (itemIdRaw.trim() === '' || testheftIdRaw.trim() === '' || Number.isInteger(itemId) === false || Number.isInteger(testheftId) === false)
            return false;

        this.render(testheftId, itemId);
        return true;
    }

    /** Baut die sichtbaren Informationen für die angeforderte Item-ID auf */
    private render(testheftId: number, itemId: number): void {
        // Itemdaten aus dem vorbereiteten Index lesen
        const data = this.itemIndex.get(this.createIndexKey(testheftId, itemId)) ?? null;

        // Verständlichen Hinweis anzeigen, falls die Item-ID nicht im geladenen Testheftbestand vorkommt
        if (data === null) {
            this.content.innerHTML = `
                <strong class="tooltip-item-title">Item ${itemId}</strong>
                <p>Für dieses Item sind keine weiteren Informationen verfügbar.</p>
            `;
            return;
        }

        // Niveaustufe vorab bestimmen, damit das folgende HTML übersichtlich bleibt
        const itemNiveaustufe = Utilities.printNiveaustufe(data.item.niveaustufe, data.testheft.fach, 'NiveaustufeBez', data.item.iqb_item_id, this.land);

        // Domäne und Schwierigkeitsstufe bilden gemeinsam mit der Position die Itembezeichnung
        const domaene = this.domaenen.find(eintrag => eintrag.domaene === data.item.domaene) ?? null;
        const domaenenBezeichnung = (data.testheft.fach === 'ma') ? '' : `${domaene?.bezeichnung ?? ''} `;
        const itemName = this.createItemBezeichnung(data);
        const modulName = `${domaenenBezeichnung}${Utilities.printModulLang(data.modul.typ)} ${data.aufgabe.position_nr}.${data.item.position_bezeichnung}`.replace(/\s+/g, ' ').trim();
        const kommentar = this.kommentarIndex.get(itemId) ?? null;
        const aufgabenansicht = (this.projektweiche === 'vera5')
            ? this.renderAufgabenansicht(data.item.cbt_item_id)
            : '';
        const aufgabenBezeichnung = data.aufgabe.bezeichnung.trim();
        const itemTitel = (aufgabenBezeichnung === '') ? itemName : `${itemName} - ${aufgabenBezeichnung}`;
        const domaenenTitel = (data.testheft.fach === 'ma') ? 'Leitidee' : 'Domäne';
        const materialien = getTooltipMaterialien(this.projektweiche, data.testheft, data.item.domaene ?? data.modul.domaene);
        const domaenenBezeichnungMitFach = (domaene === null || data.testheft.fach === 'ma') ? domaene?.bezeichnung : `${Utilities.printFachLang(data.testheft.fach)} ${domaene.bezeichnung}`;
        const domaenenWert = this.renderDomaenenWert(domaenenBezeichnungMitFach, materialien.kurzbeschreibungUrl);
        const allgemeineKompetenzTooltip = (data.testheft.fach === 'ma')
            ? TOOLTIP_UEBERSCHRIFTEN['Allgemeine Kompetenz']
            : Tooltip.html`<p>Dieses Feld zeigt den Bildungsstandard, dem die Aufgabe zugeordnet ist. Er beschreibt die fachliche Kompetenz, die mit der Aufgabe überprüft wird.</p>`;

        // Die global gültigen Informationen des gefundenen Items als beschriftete Liste ausgeben
        this.content.innerHTML = `
            <strong class="tooltip-item-title">${itemTitel}</strong>
            <dl>
                ${(this.projektweiche === 'vera5') ? '' : `
                <dt>${this.renderUeberschrift('Modul', 'collection')}</dt>
                <dd>${modulName}</dd>`}

                ${this.renderDefinition(domaenenTitel, domaenenWert, 'diagram-3')}

                ${this.renderDefinition('Item-Niveaustufe', itemNiveaustufe, 'speedometer2')}
                ${this.renderVergleichswert(data.item)}
                ${this.renderAnforderungsbereich(kommentar?.anforderungsbereich, data.testheft.fach)}
                ${(this.projektweiche === 'vera5') ? '' : this.renderKompetenzstufe(kommentar?.kompetenzstufe, data.testheft.fach, data.item.domaene ?? data.modul.domaene)}
                ${this.renderKompetenzliste('Allgemeine Kompetenz', kommentar?.bildungsstandard_allgemeine_kompetenz, 'mortarboard', allgemeineKompetenzTooltip)}
                ${this.renderKompetenzliste('Inhaltsbezogene Kompetenz', kommentar?.bildungsstandard_inhaltsbezogene_kompetenz, 'book')}
            </dl>

            ${aufgabenansicht}
            ${this.renderDidaktischeInhalte(kommentar?.kommentar_html, kommentar?.anregungen_fuer_unterricht_html)}
            ${renderTooltipDatenhinweis(materialien, 'Items', domaene?.bezeichnung)}
        `;
    }

    /** Verlinkt die konkrete Leitidee beziehungsweise Domäne mit ihrer lokalen Kurzbeschreibung */
    private renderDomaenenWert(bezeichnung: string | null | undefined, kurzbeschreibungUrl: string | null): string | null | undefined {
        if (bezeichnung === null || bezeichnung === undefined || bezeichnung.trim() === '' || kurzbeschreibungUrl === null)
            return bezeichnung;

        return `<a class="tooltip-material-link" href="${kurzbeschreibungUrl}" target="_blank" rel="noopener" title="Kurzbeschreibung öffnen">${bezeichnung}</a>`;
    }

    /** Rendert den Anforderungsbereich mit der hinterlegten Definition als generischen Tooltip */
    private renderAnforderungsbereich(stufe: number | null | undefined, fach: string): string {
        if (stufe === null || stufe === undefined)
            return '';

        const anforderungsbereich = Bildungsstandards.getAnforderungsbereich(this.projektweiche, fach, stufe);
        if (anforderungsbereich === null)
            return this.renderDefinition('Anforderungsbereich', '-', 'layers');

        const tooltipInhalt = `<p><strong>${anforderungsbereich.bezeichnung}</strong></p>${anforderungsbereich.html}`;
        return `<dt>${this.renderUeberschrift('Anforderungsbereich', 'layers')}</dt><dd><span data-tooltip="${Tooltip.htmlFromTrustedContent(tooltipInhalt)}">${anforderungsbereich.kurzbezeichnung} - ${anforderungsbereich.bezeichnung}</span></dd>`;
    }

    /** Rendert die Kompetenzstufe mit der domänenspezifischen Definition als generischen Tooltip */
    private renderKompetenzstufe(stufe: number | null | undefined, fach: string, domaene: string | null | undefined): string {
        if (stufe === null || stufe === undefined)
            return '';

        const eintraege = Bildungsstandards.getKompetenzstufenEintraege(this.projektweiche, fach, domaene, stufe);
        if (eintraege.length === 0)
            return this.renderDefinition('Kompetenzstufe', '-', 'bar-chart-steps');

        const kurzbezeichnung = eintraege.map(eintrag => eintrag.kurzbezeichnung).join('/');
        const tooltipInhalt = eintraege
            .map(eintrag => `<p><strong>${(eintraege.length === 1) ? eintrag.bezeichnung : `${eintrag.kurzbezeichnung} - ${eintrag.bezeichnung}`}</strong></p>${eintrag.html}`)
            .join('');

        return `<dt>${this.renderUeberschrift('Kompetenzstufe', 'bar-chart-steps')}</dt><dd><span data-tooltip="${Tooltip.htmlFromTrustedContent(tooltipInhalt)}">${kurzbezeichnung}</span></dd>`;
    }

    /** Rendert den zur schulischen Vergleichsgruppe passenden Referenzwert als Prozentangabe */
    private renderVergleichswert(item: ITestheftAPI_item): string {
        if (this.isVergleichswertFreigegeben === false)
            return '';

        const wert = this.verwendetGymnasialePilotierungswerte ? item.iqb_lh_gym : item.iqb_lh_andere;
        if (Number.isFinite(wert) === false)
            return '';

        const bezeichnung = (this.projektweiche === 'vera5') ? 'Landeswert' : 'Pilotierungswert';
        return this.renderDefinition(bezeichnung, `Im Schnitt von ${Math.round(wert * 100)}% richtig gelöst`, 'percent');
    }

    /** Rendert einen Wert nur, wenn die Datenbank einen Inhalt bereitstellt */
    private renderDefinition(bezeichnung: string, wert: string | number | null | undefined, icon: string): string {
        if (wert === null || wert === undefined || (typeof wert === 'string' && wert.trim() === ''))
            return '';

        return `<dt>${this.renderUeberschrift(bezeichnung, icon)}</dt><dd>${wert}</dd>`;
    }

    /** Rendert semikolongetrennte Kompetenzbeschreibungen als Liste */
    private renderKompetenzliste(bezeichnung: string, wert: string | null | undefined, icon: string, tooltip?: string): string {
        const kompetenzen = wert?.split(';').map(kompetenz => kompetenz.trim()).filter(kompetenz => kompetenz !== '') ?? [];
        if (kompetenzen.length === 0)
            return '';

        return `<dt>${this.renderUeberschrift(bezeichnung, icon, tooltip)}</dt><dd><ul class="tooltip-kompetenzliste">${kompetenzen.map(kompetenz => `<li>${kompetenz}</li>`).join('')}</ul></dd>`;
    }

    /** Verbindet eine Tooltip-Überschrift mit einem dekorativen Bootstrap-Icon */
    private renderUeberschrift(bezeichnung: string, icon: string, tooltip: string | undefined = TOOLTIP_UEBERSCHRIFTEN[bezeichnung]): string {
        const tooltipAttribut = (tooltip === undefined) ? '' : ` data-tooltip="${tooltip}"`;
        return `<svg class="tooltip-ueberschrift-icon" aria-hidden="true" focusable="false"><use href="${BOOTSTRAP_ICON_SPRITE}#${icon}"/></svg><span${tooltipAttribut}>${bezeichnung}</span>`;
    }

    /** Rendert das zur CBT-ID gehörende Itembild mit einem Fallback */
    private renderAufgabenansicht(baseAufgId: string | null): string {
        const normalizedBaseAufgId = baseAufgId?.trim() ?? '';
        if (BASE_AUFG_ID_PATTERN.test(normalizedBaseAufgId) === false) {
            return `
                <details class="tooltip-aufgabenansicht" open>
                    <summary>${this.renderUeberschrift('Aufgabenansicht', 'image')}</summary>
                    <p class="tooltip-aufgabenansicht-hinweis">Keine Aufgabenansicht verfügbar.</p>
                </details>
            `;
        }

        const bildUrl = `${AUFGABENBILD_BASISPFAD}/${encodeURIComponent(normalizedBaseAufgId)}.png`;
        return `
            <details class="tooltip-aufgabenansicht" open>
                <summary>${this.renderUeberschrift('Aufgabenansicht', 'image')}</summary>
                <div class="tooltip-aufgabenansicht-bildbereich">
                    <a class="tooltip-aufgabenansicht-bildlink" href="${bildUrl}" target="_blank" rel="noopener" aria-label="Darstellung des Items in Originalgröße öffnen" title="In Originalgröße öffnen">
                        <img class="tooltip-aufgabenansicht-bild" src="${bildUrl}" alt="Darstellung des Items">
                    </a>
                </div>
                <p class="tooltip-aufgabenansicht-hinweis" hidden>Keine Aufgabenansicht verfügbar.</p>
            </details>
        `;
    }

    /** Rendert die umfangreichen didaktischen HTML-Texte in gestuften Detailbereichen */
    private renderDidaktischeInhalte(kommentarHtml: string | null | undefined, anregungenHtml: string | null | undefined): string {
        const kommentar = kommentarHtml?.trim() ?? '';
        const anregungen = anregungenHtml?.trim() ?? '';
        if (kommentar === '' && anregungen === '')
            return '';

        return `
            <div class="tooltip-didaktik">
                ${(kommentar === '') ? '' : `
                <details open>
                    <summary>${this.renderUeberschrift('Didaktischer Kommentar', 'chat-left-text')}</summary>
                    <div class="tooltip-didaktik-html">${kommentar}</div>
                </details>`}
                ${(anregungen === '') ? '' : `
                <details open>
                    <summary>${this.renderUeberschrift('Anregungen für den Unterricht', 'lightbulb')}</summary>
                    <div class="tooltip-didaktik-html">${anregungen}</div>
                </details>`}
            </div>
        `;
    }

}
